import React, { Component } from 'react';

import {
  Button,
  Col,
  Form,
  Icon,
  Input,
  Layout,
  Popover,
  notification,
  Row,
  Select,
  Spin,
} from 'antd';

import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  DndContext,
  PointerSensor,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SelectMaterials from '../SelectMaterials';
import { techniquesService } from '../../services';
import { stringsHelp } from '../../helpers';
import { selectsActions } from '../../actions';
import { mixPanelService } from '../../services/mixpanel.service';
import Api from '../../helpers/api';

const FormItem = Form.Item;
const { Content } = Layout;
const { Option } = Select;

// Technique é um componente de classe; useSortable exige um componente de função.
function SortableStep({
  id, disabled, style, children,
}) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id, disabled });

  const wrapperStyle = {
    ...style,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div ref={setNodeRef} style={wrapperStyle}>
      {children({ dragHandleProps: { ...attributes, ...listeners } })}
    </div>
  );
}

const STEP_TYPE_ITEMS = [
  { key: 'FREE_TEXT', label: 'Descrição livre' },
  { key: 'POSITION', label: 'Posição' },
  { key: 'DRUG', label: 'Fármaco' },
  { key: 'NEURAXIAL_NEEDLE', label: 'Agulha bloqueio neuroeixo' },
  { key: 'IV_CATHETER', label: 'Cateter endovenoso' },
  { key: 'TUBE', label: 'Tubo' },
  { key: 'PROBE', label: 'Sonda' },
  { key: 'CANNULA', label: 'Cânula de Quedell' },
  { key: 'OTHER_EQUIPMENT', label: 'Outros equipamentos' },
];

const STATIC_OPTIONS_BY_TYPE = {
  NEURAXIAL_NEEDLE: ['Quincke', 'Tuohy', 'Tohy Borst', 'Whitacre', 'Weiss'],
};

const TECHNIQUE_SAVE_ERROR_MESSAGES = {
  DUPLICATED: 'Já existe uma técnica cadastrada com mesmo nome e categoria',
  INVALID_MATERIALS: 'Um ou mais materiais selecionados não pertencem a esta instituição',
};

const POSITION_IMAGES = (() => {
  try {
    const ctx = require.context('../../assets/positions', false, /\.png$/);
    return ctx.keys().reduce((acc, key) => {
      const name = key.replace('./', '').replace('.png', '');
      const mod = ctx(key);
      const src = (mod && mod.default ? mod.default : null)
        || (mod && mod.src ? mod.src : null)
        || (typeof mod === 'string' ? mod : null)
        || mod;
      acc[name] = src;
      return acc;
    }, {});
  } catch (e) {
    return {};
  }
})();

const RESOURCE_TYPE_BY_STEP_TYPE = {
  NEURAXIAL_NEEDLE: 'NEEDLE',
  IV_CATHETER: 'CATETER',
  TUBE: 'TUBE',
  PROBE: 'SONDA',
  CANNULA: 'CANULA',
  OTHER_EQUIPMENT: 'OTHER',
};

class Technique extends Component {
  freeTextInputRefs = {};

  dndSensors = [
    { sensor: PointerSensor, options: { activationConstraint: { distance: 8 } } },
  ];

  state = {
    technique: {},
    techniqueCategories: [],
    loadingCategories: true,
    loading: false,
    searchInterval: null,
    categoryId: null,
    steps: [],
    stepTypeModalVisible: false,
    stepValueModalVisible: false,
    stepValueType: null,
    stepValueSearch: '',
    stepValueText: '',
    stepValueOptions: [],
    stepValueLoading: false,
    positionsCatalog: null,
    resourceTypesCatalog: null,
    resourcesCatalog: null,
    resourceSpecializationsCatalog: null,
    stepValueResourceId: null,
    stepValueResourceName: '',
    stepValueSelectedOptionId: null,
    stepValueDrugAmount: '',
    stepValuePage: 1,
    stepValueHasMore: true,
    stepValueLoadingMore: false,
    editingStepId: null,
  };

  componentDidMount() {
    this.loadTechniqueCategories();
    
    const { match, form, history } = this.props;

    this.ensurePositionsCatalog().catch(() => {});

    if (match.params.id) {
      this.setState({ loading: true });

      techniquesService.getTechnique(match.params.id)
        .then((technique) => {
          const techniqueData = technique || {};

          if (!techniqueData.id) {
            throw new Error('technique.not.found');
          }

          const categoryId = techniqueData.technique_category_id
            || (
              techniqueData.anesthetic_technique_category
              && techniqueData.anesthetic_technique_category.id
            )
            || null;
          const steps = this.parseStepsFromDescription(techniqueData.description);

          this.setState({
            technique: techniqueData, loading: false, categoryId, steps,
          }, () => {
            form.setFieldsValue({
              categoryId,
              name: techniqueData.name,
              materials: techniqueData.materials,
            });
          });
        })
        .catch(() => {
          notification.error({
            message: 'Não foi possível recuperar a técnica anestésica.',
          });
          history.goBack();
        });
    } else {
      const steps = this.parseStepsFromDescription('');
      this.setState({ steps });
    }
  }

  normalizeText = (value) => String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  buildDrugDisplayLabel = (dci, lasa) => {
    const lasaRaw = typeof lasa === 'string' ? lasa : '';
    const base = lasaRaw && lasaRaw.includes('<lasa>') ? lasaRaw : String(dci || '');
    const text = String(base || '').trim();
    if (!text) return null;

    if (!text.includes('<lasa>')) {
      return <span className="technique-drug-label">{text.toLowerCase()}</span>;
    }

    const parts = [];
    const re = /<lasa>(.*?)<\/lasa>/gi;
    let lastIndex = 0;
    let match = re.exec(text);
    while (match) {
      const start = match.index;
      const end = match.index + match[0].length;
      const before = text.slice(lastIndex, start);
      if (before) parts.push({ key: `n_${lastIndex}_${start}`, isLasa: false, text: before });
      parts.push({ key: `l_${start}_${end}`, isLasa: true, text: match[1] || '' });
      lastIndex = end;
      match = re.exec(text);
    }
    const after = text.slice(lastIndex);
    if (after) parts.push({ key: `n_${lastIndex}_${text.length}`, isLasa: false, text: after });

    return (
      <span className="technique-drug-label">
        {parts.map((p) => (p.isLasa ? (
          <span key={p.key} className="technique-drug-lasa">
            {String(p.text || '').toUpperCase()}
          </span>
        ) : (
          <span key={p.key}>{String(p.text || '').toLowerCase()}</span>
        )))}
      </span>
    );
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const { form, history } = this.props;
    const { categoryId, steps, technique: currentTechnique } = this.state;

    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.setState({ loading: true });

        const rawMaterials = values.materials;
        const materials = (() => {
          if (!rawMaterials) return [];
          if (typeof rawMaterials.toArray === 'function') {
            return rawMaterials
              .map((material) => (material && material.id ? material.id : null))
              .toArray()
              .filter(Boolean);
          }
          if (Array.isArray(rawMaterials)) {
            return rawMaterials
              .map((material) => {
                if (material && typeof material === 'object' && material.id) return material.id;
                return material;
              })
              .filter(Boolean);
          }
          return [];
        })();
        const resolvedCategoryId = values.categoryId || categoryId;
        const description = this.buildDescriptionFromSteps(steps);

        const nextValues = {
          ...values,
          materials,
          active: true,
          category_id: resolvedCategoryId,
          description,
        };

        const technique = { ...currentTechnique, ...nextValues };

        let promiseSave;
        if (technique.id > 0) {
          mixPanelService.tracking(mixPanelService.TECNICA_EDITAR);

          const updateData = {
            active: technique.active,
            category_id: resolvedCategoryId,
            name: technique.name,
            description: technique.description,
            materials: technique.materials,
          };

          promiseSave = techniquesService.putTechnique(technique.id, updateData)
            .then((result) => {
              if (result.id > 0) {
                this.setState({ loading: false });

                notification.success({
                  message: 'Técnica alterada com sucesso',
                });

                history.goBack();
              } else {
                let msg = 'Erro ao editar técnica anestésica';

                if (result && result.data && result.data.msg) {
                  msg = result.data.msg;
                }

                throw new Error(msg);
              }
            })
        } else {
          mixPanelService.tracking(mixPanelService.TECNICA_CRIAR);

          const createData = {
            active: technique.active,
            category_id: resolvedCategoryId,
            name: technique.name,
            description: technique.description,
            materials: technique.materials,
          };

          promiseSave = techniquesService.postTechnique(createData)
            .then((result) => {
              if (result.id > 0) {
                this.setState({ loading: false });
                notification.success({
                  message: 'Técnica cadastrada com sucesso'
                });

                history.goBack();
              } else {
                let msg = 'Erro ao cadastrar técnica anestésica';

                if (result && result.data && result.data.msg) {
                  msg = result.data.msg;
                }

                throw new Error(msg);
              }
            });
        }
        promiseSave.catch((error) => {
          let msg = `Erro na operação`;
          const status = error && error.data && error.data.data && error.data.data.status;
          if (status && TECHNIQUE_SAVE_ERROR_MESSAGES[status]) {
            msg = TECHNIQUE_SAVE_ERROR_MESSAGES[status];
          } else if (error && error.response && error.response.data && error.response.data.msg) {
            msg = error.response.data.msg;
          } else if (error && error.message) {
            msg = error.message;
          }

          this.setState({ loading: false });

          notification.error({
            message: msg,
          });
        });
      }
    });
  };

  validatorTechniqueName = async (rule, value, callback) => {
    if (!value || value === '' || value === undefined) {
      callback();
      return null;
    }

    // const unique = await eventsService
    //   .getTransEventPresetsByName(value)
    //   .then((event) => {
    //     event = event || {};
    //     return event[0];
    //   }).catch(() => false);

    // if (unique) {
    //   callback(rule.message);
    //   return null;
    // }
  }

  loadTechniqueCategories = async () => {
    this.setState({ loadingCategories: true });
    let categories = [];
    try {
      const data = await techniquesService.getTechniqueCategories(
        this.cancelSource ? this.cancelSource.token : undefined,
      );
      categories = Array.isArray(data) ? data : [];
      this.setState({
        techniqueCategories: categories,
        loadingCategories: false,
      });
    } catch {
      this.setState({
        techniqueCategories: [],
        loadingCategories: false,
      });
      notification.error({
        message: 'Não foi possível carregar as categorias.',
      });
    }

    const { form } = this.props;
    const current = form.getFieldValue('categoryId');
    if (!current && categories.length > 0) {
      form.setFieldsValue({ categoryId: categories[0].id });
      this.setCategory(categories[0].id);
    }
  };

  setCategory = (value) => {
    this.setState({ categoryId: value || null });
  };

  parseStepsFromDescription = (description) => {
    const lines = (typeof description === 'string' ? description : '')
      .split('\n')
      .map((l) => (l || '').trim())
      .filter((l) => l.length > 0);

    if (lines.length === 0) return [];

    return lines.map((text) => ({
      id: this.newStepId(),
      typeKey: 'FREE_TEXT',
      optionId: null,
      text,
    }));
  };

  buildDescriptionFromSteps = (steps) => (steps || [])
    .map((s) => (s && s.text ? String(s.text).trim() : ''))
    .filter((t) => t.length > 0)
    .join('\n');

  newStepId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

  openStepTypeModal = (maybeEditingStepId) => {
    const editingStepId = typeof maybeEditingStepId === 'string' ? maybeEditingStepId : null;
    this.setState({
      stepTypeModalVisible: true,
      stepValueModalVisible: false,
      stepValueType: null,
      stepValueSearch: '',
      stepValueText: '',
      stepValueOptions: [],
      stepValueLoading: false,
      stepValueResourceId: null,
      stepValueResourceName: '',
      stepValueSelectedOptionId: null,
      stepValueDrugAmount: '',
      editingStepId,
    });
    this.ensureResourceCatalogs().catch(() => {});
  };

  closeStepTypeModal = () => {
    this.setState({ stepTypeModalVisible: false });
  };

  closeStepValueModal = () => {
    this.setState({
      stepValueModalVisible: false,
      stepTypeModalVisible: false,
      stepValueType: null,
      stepValueSearch: '',
      stepValueText: '',
      stepValueOptions: [],
      stepValueLoading: false,
      stepValueLoadingMore: false,
      stepValuePage: 1,
      stepValueHasMore: true,
      stepValueResourceId: null,
      stepValueResourceName: '',
      stepValueSelectedOptionId: null,
      stepValueDrugAmount: '',
      editingStepId: null,
    });
  };

  setFreeTextStepText = (id, text) => {
    if (!id) return;
    this.setState((prev) => ({
      steps: (prev.steps || []).map((s) => (s.id === id ? { ...s, text } : s)),
    }));
  };

  finalizeFreeTextStep = (id) => {
    if (!id) return;
    this.setState((prev) => {
      const steps = prev.steps || [];
      const current = steps.find((s) => s.id === id);
      if (!current) return null;

      const clean = String(current.text || '').trim();
      if (!clean) {
        return { steps: steps.filter((s) => s.id !== id) };
      }

      return {
        steps: steps.map((s) => (s.id === id ? { ...s, text: clean } : s)),
      };
    });
  };

  onSelectStepType = (typeKey) => {
    const key = String(typeKey || '').toUpperCase();
    if (!key) return;

    const { editingStepId } = this.state;

    if (key === 'FREE_TEXT') {
      if (editingStepId) {
        this.setState(
          (prev) => ({
            steps: (prev.steps || []).map((s) => (s.id === editingStepId
              ? { ...s, typeKey: 'FREE_TEXT', optionId: null }
              : s)),
            stepTypeModalVisible: false,
            stepValueModalVisible: false,
            editingStepId: null,
          }),
          () => {
            const ref = this.freeTextInputRefs[editingStepId];
            if (ref && typeof ref.focus === 'function') ref.focus();
          },
        );
        return;
      }

      const id = this.newStepId();
      this.setState(
        (prev) => ({
          steps: [...(prev.steps || []), {
            id,
            typeKey: 'FREE_TEXT',
            optionId: null,
            text: '',
          }],
          stepTypeModalVisible: false,
          stepValueModalVisible: false,
          editingStepId: null,
        }),
        () => {
          const ref = this.freeTextInputRefs[id];
          if (ref && typeof ref.focus === 'function') ref.focus();
        },
      );
      return;
    }

    this.openStepValueModalForType(key);
  };

  openStepValueModalForType = (typeKey) => {
    this.setState(
      {
        stepTypeModalVisible: false,
        stepValueModalVisible: true,
        stepValueType: typeKey,
        stepValueSearch: '',
        stepValueText: '',
        stepValueOptions: [],
        stepValueLoading: false,
        stepValueLoadingMore: false,
        stepValuePage: 1,
        stepValueHasMore: true,
        stepValueResourceId: null,
        stepValueResourceName: '',
        stepValueSelectedOptionId: null,
        stepValueDrugAmount: '',
      },
      () => {
        this.loadStepOptions(typeKey, '');
      },
    );
  };

  ensurePositionsCatalog = async () => {
    const { positionsCatalog } = this.state;
    if (Array.isArray(positionsCatalog)) return positionsCatalog;
    const list = await Api.get('/app/positions');
    const parsed = Array.isArray(list) ? list : [];
    this.setState({ positionsCatalog: parsed });
    return parsed;
  };

  getPositionIconSrcByStyledKey = (styledKey) => {
    const key = String(styledKey || '').trim();
    if (!key) return null;

    if (POSITION_IMAGES[key]) return POSITION_IMAGES[key];

    const lowerKey = key.toLowerCase();
    if (POSITION_IMAGES[lowerKey]) return POSITION_IMAGES[lowerKey];

    if (lowerKey.startsWith('positiontype')) {
      const raw = key.slice('PositionType'.length);
      const snake = raw
        .replace(/\s+/g, '')
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .replace(/-/g, '_')
        .toLowerCase();
      const fileKey = `icon_position_type_${snake}`;
      if (POSITION_IMAGES[fileKey]) return POSITION_IMAGES[fileKey];
    }

    if (!lowerKey.startsWith('icon_position_type_')) {
      const fileKey = `icon_position_type_${lowerKey.replace(/\s+/g, '_').replace(/-/g, '_')}`;
      if (POSITION_IMAGES[fileKey]) return POSITION_IMAGES[fileKey];
    }

    return null;
  };

  getPositionIconSrcById = (positionId) => {
    const id = Number(positionId);
    if (!id) return null;
    const { positionsCatalog } = this.state;
    const positions = positionsCatalog;
    const position = (positions || []).find((p) => Number(p && p.id) === id) || null;
    const key = position && position.styled_image_key ? String(position.styled_image_key).trim() : '';
    if (!key) return null;
    return this.getPositionIconSrcByStyledKey(key);
  };

  ensureResourceCatalogs = async () => {
    const { resourceTypesCatalog, resourcesCatalog, resourceSpecializationsCatalog } = this.state;
    const hasAll = Array.isArray(resourceTypesCatalog)
      && Array.isArray(resourcesCatalog)
      && Array.isArray(resourceSpecializationsCatalog);

    if (hasAll) {
      return {
        resourceTypes: resourceTypesCatalog,
        resources: resourcesCatalog,
        resourceSpecializations: resourceSpecializationsCatalog,
      };
    }

    const [resourceTypesRaw, resourcesRaw, resourceSpecializationsRaw] = await Promise.all([
      Api.get('/app/resource-types'),
      Api.get('/app/resources'),
      Api.get('/app/resource-specializations'),
    ]);

    const resourceTypes = Array.isArray(resourceTypesRaw) ? resourceTypesRaw : [];
    const resources = Array.isArray(resourcesRaw) ? resourcesRaw : [];
    const resourceSpecializations = Array.isArray(resourceSpecializationsRaw)
      ? resourceSpecializationsRaw
      : [];

    this.setState({
      resourceTypesCatalog: resourceTypes,
      resourcesCatalog: resources,
      resourceSpecializationsCatalog: resourceSpecializations,
    });

    return { resourceTypes, resources, resourceSpecializations };
  };

  getStepTypeItems = () => {
    const { resourceTypesCatalog } = this.state;
    const resourceTypes = Array.isArray(resourceTypesCatalog) ? resourceTypesCatalog : [];
    const labelByResourceType = resourceTypes.reduce((acc, rt) => {
      const type = rt && rt.type ? String(rt.type).trim().toUpperCase() : '';
      if (!type) return acc;
      const label = this.getCatalogName(rt, 'resource_type');
      if (!label) return acc;
      acc[type] = label;
      return acc;
    }, {});

    return STEP_TYPE_ITEMS.map((item) => {
      const key = item && item.key ? String(item.key).toUpperCase() : '';
      if (!key || key === 'FREE_TEXT') return item;

      const mappedType = RESOURCE_TYPE_BY_STEP_TYPE[key] || key;
      const labelFromApi = labelByResourceType[mappedType];
      if (!labelFromApi) return item;
      return { ...item, label: labelFromApi };
    });
  };

  getStepTypeLabel = (typeKey) => {
    const items = this.getStepTypeItems();
    const item = items.find((x) => x.key === typeKey);
    return item && item.label ? item.label : '';
  };

  getCatalogName = (entity, i18nKeyBase) => {
    if (!entity) return '';
    if (entity.name) return String(entity.name).trim();

    const single = entity[`${i18nKeyBase}_i18n`];
    if (single) {
      if (Array.isArray(single)) return String((single[0] && single[0].name) || '').trim();
      return String(single.name || '').trim();
    }

    const many = entity[`${i18nKeyBase}_i18ns`];
    if (many) {
      if (Array.isArray(many)) return String((many[0] && many[0].name) || '').trim();
      return String(many.name || '').trim();
    }

    return '';
  };

  getResourceTypeIdsForStepType = (stepTypeKey, resourceTypes) => {
    const upperKey = String(stepTypeKey || '').toUpperCase();
    const mappedKey = RESOURCE_TYPE_BY_STEP_TYPE[upperKey] || upperKey;
    const direct = (resourceTypes || [])
      .filter((rt) => String(rt && rt.type ? rt.type : '').toUpperCase() === mappedKey)
      .map((rt) => rt.id)
      .filter((id) => Number.isInteger(id));

    if (direct.length > 0) return direct;

    const itemLabel = this.getStepTypeLabel(stepTypeKey);
    const byLabel = this.normalizeText(itemLabel || '');
    if (!byLabel) return [];

    return (resourceTypes || [])
      .filter((rt) => {
        const name = this.normalizeText(rt && rt.name ? rt.name : '');
        if (!name) return false;
        return name === byLabel || name.includes(byLabel) || byLabel.includes(name);
      })
      .map((rt) => rt.id)
      .filter((id) => Number.isInteger(id));
  };

  loadStepOptions = async (typeKey, search, opts = {}) => {
    if (typeKey === 'FREE_TEXT') {
      this.setState({ stepValueOptions: [], stepValueLoading: false });
      return;
    }

    if (typeKey === 'POSITION') {
      const q = this.normalizeText(search || '');
      this.setState({ stepValueLoading: true });
      try {
        const positions = await this.ensurePositionsCatalog();
        const options = (positions || [])
          .map((p) => {
            const positionI18n = p && p.position_i18n ? p.position_i18n : null;
            const positionI18ns = p && p.position_i18ns ? p.position_i18ns : null;

            let nameRaw = p && p.name ? p.name : '';
            if (!nameRaw && positionI18n) {
              if (Array.isArray(positionI18n)) {
                nameRaw = positionI18n[0] && positionI18n[0].name ? positionI18n[0].name : '';
              } else {
                nameRaw = positionI18n.name;
              }
            }
            if (!nameRaw && positionI18ns) {
              if (Array.isArray(positionI18ns)) {
                nameRaw = positionI18ns[0] && positionI18ns[0].name ? positionI18ns[0].name : '';
              } else {
                nameRaw = positionI18ns.name;
              }
            }

            const fallbackLabel = `Posição ${p && p.id ? p.id : ''}`.trim();
            const label = String(nameRaw || '').trim() || fallbackLabel;
            const imageKey = p && p.styled_image_key ? String(p.styled_image_key).trim() : '';
            const imageSrc = imageKey ? this.getPositionIconSrcByStyledKey(imageKey) : null;

            return {
              id: p && p.id ? p.id : null,
              label,
              imageKey,
              imageSrc,
            };
          })
          .filter((opt) => opt.id && this.normalizeText(opt.label).includes(q));
        this.setState({ stepValueOptions: options, stepValueLoading: false });
      } catch {
        this.setState({ stepValueOptions: [], stepValueLoading: false });
      }
      return;
    }

    if (typeKey === 'DRUG') {
      const filter = typeof search === 'string' ? search : '';
      const page = opts && opts.page ? Number(opts.page) : 1;
      const append = !!(opts && opts.append);
      const limit = 20;

      if (append && page > 1) {
        this.setState({ stepValueLoadingMore: true });
      } else {
        this.setState({ stepValueLoading: true, stepValueLoadingMore: false });
      }

      try {
        const selectedInstitutionRaw = localStorage.getItem('selected-institution') || '{}';
        let selectedInstitution = {};
        try {
          selectedInstitution = JSON.parse(selectedInstitutionRaw);
        } catch (err) {
          selectedInstitution = {};
        }
        const institutionIdRaw = selectedInstitution
          && (selectedInstitution.institution_id || selectedInstitution.id)
          ? (selectedInstitution.institution_id || selectedInstitution.id)
          : null;
        const institutionId = institutionIdRaw != null ? Number(institutionIdRaw) : null;

        const params = {
          page: Number.isFinite(page) && page > 0 ? page : 1,
          limit,
          filter,
        };
        if (institutionId) {
          params.institution = institutionId;
        }

        const res = await Api.get('/preset/v2/active-principles', {
          params,
        });

        let list = [];
        if (Array.isArray(res)) {
          list = res;
        } else if (res && Array.isArray(res.activePrinciples)) {
          list = res.activePrinciples;
        }

        const options = (list || [])
          .map((d) => {
            const id = d && d.id ? d.id : null;
            const dciRaw = (d && d.dci)
              || (
                d
                && d.active_principle_i18n
                && (d.active_principle_i18n.dci || d.active_principle_i18n.name)
              )
              || (d && d.name)
              || '';

            const dci = String(dciRaw || '').trim();

            const lasaRaw = d && d.active_principle_i18n && d.active_principle_i18n.lasa
              ? d.active_principle_i18n.lasa
              : '';
            const displayLabel = this.buildDrugDisplayLabel(dci, lasaRaw);

            return { id, label: dci, displayLabel };
          })
          .filter((opt) => opt.id && opt.label);

        this.setState((prev) => {
          const merged = append ? [...(prev.stepValueOptions || []), ...(options || [])] : (options || []);
          const unique = [];
          const seen = new Set();
          merged.forEach((o) => {
            const key = o && o.id != null ? String(o.id) : '';
            if (!key || seen.has(key)) return;
            seen.add(key);
            unique.push(o);
          });

          return {
            stepValueOptions: unique,
            stepValueLoading: false,
            stepValueLoadingMore: false,
            stepValuePage: params.page,
            stepValueHasMore: (list || []).length >= limit,
          };
        });
      } catch {
        this.setState({ stepValueLoading: false, stepValueLoadingMore: false, stepValueHasMore: false });
      }
      return;
    }

    const shouldUseResourceCatalog = typeKey === 'NEURAXIAL_NEEDLE'
      || typeKey === 'IV_CATHETER'
      || typeKey === 'TUBE'
      || typeKey === 'PROBE'
      || typeKey === 'CANNULA'
      || typeKey === 'OTHER_EQUIPMENT';

    if (!shouldUseResourceCatalog) {
      this.setState({ stepValueOptions: [], stepValueLoading: false });
      return;
    }

    const q = this.normalizeText(search || '');
    this.setState({ stepValueLoading: true });

    try {
      const {
        resourceTypes,
        resources,
        resourceSpecializations,
      } = await this.ensureResourceCatalogs();
      const resourceTypeIds = this.getResourceTypeIdsForStepType(typeKey, resourceTypes);

      const resourcesOfType = (resources || []).filter((r) => {
        const resourceTypeId = Number(r && r.resource_type_id);
        return resourceTypeIds.includes(resourceTypeId);
      });

      const specializationsByResourceId = (resourceSpecializations || []).reduce((acc, spec) => {
        const resourceId = spec && spec.resource_id ? spec.resource_id : null;
        if (!resourceId) return acc;
        if (!acc[resourceId]) acc[resourceId] = [];
        acc[resourceId].push(spec);
        return acc;
      }, {});

      const { stepValueResourceId } = this.state;
      const options = [];

      if (!stepValueResourceId) {
        resourcesOfType.forEach((r) => {
          const resourceName = this.getCatalogName(r, 'resource') || `Recurso ${r && r.id ? r.id : ''}`.trim();
          if (!resourceName) return;
          const specs = specializationsByResourceId[r.id] || [];
          options.push({
            id: String(r.id),
            label: resourceName,
            kind: 'resource',
            hasChildren: specs.length > 0,
          });
        });
      } else {
        const resourceId = Number(stepValueResourceId);
        const specs = specializationsByResourceId[resourceId] || [];
        specs.forEach((spec) => {
          const specName = spec && spec.name ? String(spec.name).trim() : '';
          if (!specName) return;
          options.push({
            id: `${resourceId}:${spec.id}`,
            label: specName,
            kind: 'specialization',
            resourceId,
            specializationId: spec.id,
          });
        });
      }

      const filtered = options.filter((opt) => this.normalizeText(opt.label).includes(q));

      if (filtered.length > 0) {
        this.setState({ stepValueOptions: filtered, stepValueLoading: false });
        return;
      }

      const staticOptions = STATIC_OPTIONS_BY_TYPE[typeKey];
      if (Array.isArray(staticOptions)) {
        const fallback = staticOptions
          .filter((x) => this.normalizeText(x).includes(q))
          .map((x) => ({ id: x, label: x }));
        this.setState({ stepValueOptions: fallback, stepValueLoading: false });
        return;
      }

      this.setState({ stepValueOptions: [], stepValueLoading: false });
    } catch {
      const staticOptions = STATIC_OPTIONS_BY_TYPE[typeKey];
      if (Array.isArray(staticOptions)) {
        const fallback = staticOptions
          .filter((x) => this.normalizeText(x).includes(q))
          .map((x) => ({ id: x, label: x }));
        this.setState({ stepValueOptions: fallback, stepValueLoading: false });
        return;
      }

      this.setState({ stepValueOptions: [], stepValueLoading: false });
    }
  };

  backToResourceList = () => {
    const { stepValueType } = this.state;
    this.setState(
      {
        stepValueResourceId: null,
        stepValueResourceName: '',
        stepValueSearch: '',
        stepValueOptions: [],
        stepValueLoading: false,
        stepValueText: '',
        stepValueSelectedOptionId: null,
        stepValueDrugAmount: '',
      },
      () => {
        this.loadStepOptions(stepValueType, '');
      },
    );
  };

  backToStepTypeList = () => {
    this.setState({
      stepTypeModalVisible: true,
      stepValueModalVisible: false,
      stepValueType: null,
      stepValueSearch: '',
      stepValueText: '',
      stepValueOptions: [],
      stepValueLoading: false,
      stepValueLoadingMore: false,
      stepValuePage: 1,
      stepValueHasMore: true,
      stepValueResourceId: null,
      stepValueResourceName: '',
      stepValueSelectedOptionId: null,
      stepValueDrugAmount: '',
    });
  };

  onStepValueSearchChange = (e) => {
    const value = e && e.target ? e.target.value : '';
    const search = typeof value === 'string' ? value : '';

    this.setState((prev) => ({
      stepValueSearch: search,
      stepValuePage: prev.stepValueType === 'DRUG' ? 1 : prev.stepValuePage,
      stepValueHasMore: prev.stepValueType === 'DRUG' ? true : prev.stepValueHasMore,
      stepValueOptions: prev.stepValueType === 'DRUG' ? [] : prev.stepValueOptions,
      stepValueLoadingMore: prev.stepValueType === 'DRUG' ? false : prev.stepValueLoadingMore,
    }), () => {
      const { stepValueType, stepValueSearch } = this.state;
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.loadStepOptions(stepValueType, stepValueSearch, { page: 1 });
      }, 250);
    });
  };

  onStepValueListScroll = (e) => {
    const {
      stepValueType,
      stepValueLoading,
      stepValueLoadingMore,
      stepValueHasMore,
      stepValuePage,
      stepValueSearch,
    } = this.state;

    if (stepValueType !== 'DRUG') return;
    if (stepValueLoading || stepValueLoadingMore || !stepValueHasMore) return;

    const target = e && e.target ? e.target : null;
    if (!target) return;

    const thresholdPx = 80;
    const reachedBottom = (target.scrollTop + target.clientHeight) >= (target.scrollHeight - thresholdPx);
    if (!reachedBottom) return;

    this.loadStepOptions('DRUG', stepValueSearch, { page: stepValuePage + 1, append: true });
  };

  addStepWithText = (text, typeKey, optionId) => {
    const clean = (text || '').trim();
    if (!clean) return;

    const next = {
      id: this.newStepId(),
      typeKey: typeKey || 'FREE_TEXT',
      optionId: optionId || null,
      text: clean,
    };
    this.setState(
      (prev) => ({ steps: [...(prev.steps || []), next] }),
      () => this.closeStepValueModal(),
    );
  };

  replaceStepWithText = (id, text, typeKey, optionId) => {
    const clean = (text || '').trim();
    if (!id) return;
    if (!clean) return;

    this.setState(
      (prev) => ({
        steps: (prev.steps || []).map((s) => (s.id === id
          ? {
            ...s,
            typeKey: typeKey || s.typeKey || 'FREE_TEXT',
            optionId: optionId || null,
            text: clean,
          }
          : s)),
      }),
      () => this.closeStepValueModal(),
    );
  };

  commitCurrentStepValue = (override = {}) => {
    const {
      editingStepId,
      stepValueType,
      stepValueText,
      stepValueSelectedOptionId,
      stepValueDrugAmount,
    } = this.state;
    const typeKey = override.typeKey || stepValueType || 'FREE_TEXT';

    const text = typeof override.text === 'string' ? override.text : stepValueText;
    const optionId = override.optionId != null ? override.optionId : stepValueSelectedOptionId;
    const amount = typeof override.amount === 'string' ? override.amount : stepValueDrugAmount;

    const cleanText = String(text || '').trim();
    if (!cleanText) return;

    const cleanAmount = String(amount || '').trim();
    const finalText = typeKey === 'DRUG' && cleanAmount
      ? `${cleanText} - ${cleanAmount}`
      : cleanText;

    if (editingStepId) {
      this.replaceStepWithText(editingStepId, finalText, typeKey, optionId);
      return;
    }
    this.addStepWithText(finalText, typeKey, optionId);
  };

  onSelectStepValueOption = (opt) => {
    const kind = opt && opt.kind ? opt.kind : null;
    const label = opt && opt.label ? opt.label : '';
    const optionId = opt && opt.id ? opt.id : null;
    const { stepValueType, resourcesCatalog } = this.state;
    const typeKey = stepValueType || 'FREE_TEXT';

    const isResourceCatalogType = typeKey === 'NEURAXIAL_NEEDLE'
      || typeKey === 'IV_CATHETER'
      || typeKey === 'TUBE'
      || typeKey === 'PROBE'
      || typeKey === 'CANNULA'
      || typeKey === 'OTHER_EQUIPMENT';

    if (isResourceCatalogType && kind === 'resource') {
      const resourceId = opt && opt.id ? Number(opt.id) : null;
      const selectedRaw = (resourcesCatalog || []).find((r) => {
        const id = Number(r && r.id);
        return id === resourceId;
      });
      const selected = selectedRaw || null;
      const resourceName = this.getCatalogName(selected, 'resource')
        || String(label || '').trim()
        || `Recurso ${resourceId || ''}`.trim();

      if (opt && opt.hasChildren) {
        this.setState(
          {
            stepValueResourceId: resourceId,
            stepValueResourceName: resourceName,
            stepValueSearch: '',
            stepValueOptions: [],
            stepValueLoading: false,
            stepValueText: '',
            stepValueSelectedOptionId: null,
          },
          () => {
            this.loadStepOptions(typeKey, '');
          },
        );
        return;
      }

      const nextOptionId = String(resourceId);
      this.setState(
        { stepValueText: resourceName, stepValueSelectedOptionId: nextOptionId },
        () => this.commitCurrentStepValue({ text: resourceName, optionId: nextOptionId, typeKey }),
      );
      return;
    }

    if (isResourceCatalogType && kind === 'specialization') {
      const { stepValueResourceName, stepValueResourceId } = this.state;
      const composed = `${String(stepValueResourceName || '').trim()} - ${String(label || '').trim()}`.trim();
      const composedId = optionId || `${stepValueResourceId}:${opt && opt.specializationId ? opt.specializationId : ''}`;

      this.setState(
        { stepValueText: composed, stepValueSelectedOptionId: composedId },
        () => this.commitCurrentStepValue({ text: composed, optionId: composedId, typeKey }),
      );
      return;
    }

    if (typeKey === 'DRUG') {
      this.setState(
        { stepValueText: label, stepValueSelectedOptionId: optionId, stepValueDrugAmount: '' },
        () => {
          if (this.drugAmountInput && typeof this.drugAmountInput.focus === 'function') {
            this.drugAmountInput.focus();
          }
        },
      );
      return;
    }

    this.setState(
      { stepValueText: label, stepValueSelectedOptionId: optionId },
      () => this.commitCurrentStepValue({ text: label, optionId, typeKey }),
    );
  };

  removeStep = (id) => {
    this.setState((prev) => ({
      steps: (prev.steps || []).filter((s) => s.id !== id),
    }));
  };

  openEditStep = (stepId) => {
    if (!stepId) return;

    this.setState({
      stepTypeModalVisible: true,
      stepValueModalVisible: false,
      stepValueType: null,
      stepValueSearch: '',
      stepValueText: '',
      stepValueOptions: [],
      stepValueLoading: false,
      stepValueResourceId: null,
      stepValueResourceName: '',
      stepValueSelectedOptionId: null,
      stepValueDrugAmount: '',
      editingStepId: stepId,
    });
  };

  onStepDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    this.setState((prev) => {
      const steps = prev.steps || [];
      const oldIndex = steps.findIndex((s) => s.id === active.id);
      const newIndex = steps.findIndex((s) => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return null;
      return { steps: arrayMove(steps, oldIndex, newIndex) };
    });
  };

  render() {
    const { match, form } = this.props;
    const { getFieldDecorator } = form;

    const {
      technique, loading, loadingCategories, techniqueCategories, steps,
    } = this.state;
    const {
      stepTypeModalVisible,
      stepValueModalVisible,
      stepValueSearch,
      stepValueLoading,
      stepValueOptions,
      stepValueSelectedOptionId,
      stepValueType,
      stepValueText,
      stepValueDrugAmount,
      stepValueResourceId,
      stepValueResourceName,
      stepValueLoadingMore,
      editingStepId,
    } = this.state;
    const isEdit = match.params.id;

    const isDisabled = technique.active === false;
    const popoverVisible = stepTypeModalVisible || stepValueModalVisible;

    const popoverTitle = (() => {
      if (stepTypeModalVisible) {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 24,
            }}
          >
            {editingStepId ? 'Editar passo' : 'Adicionar passo'}
          </div>
        );
      }

      const typeLabel = this.getStepTypeLabel(stepValueType);
      const resourceName = stepValueResourceName;
      const titleText = resourceName ? `${typeLabel} > ${resourceName}` : typeLabel;

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button
            shape="default"
            size="small"
            type="link"
            onClick={() => {
              if (stepValueResourceId) {
                this.backToResourceList();
                return;
              }
              this.backToStepTypeList();
            }}
          >
            <Icon type="arrow-left" />
          </Button>
          <div style={{
            flex: 1, textAlign: 'center', fontWeight: 600, color: '#262626',
          }}
          >
            {titleText}
          </div>
          <div style={{ width: 24 }} />
        </div>
      );
    })();

    const popoverContent = stepTypeModalVisible ? (
      <div style={{
        width: 360, maxHeight: 520, overflowY: 'auto', padding: 8,
      }}
      >
        {this.getStepTypeItems().map((item, idx) => (
          <button
            type="button"
            key={item.key}
            onClick={() => this.onSelectStepType(item.key)}
            style={{
              width: '100%',
              padding: '12px 8px',
              cursor: 'pointer',
              borderBottom:
                idx === STEP_TYPE_ITEMS.length - 1 ? 'none' : '1px solid #f0f0f0',
              textAlign: 'center',
              fontSize: 16,
              color: '#595959',
              background: 'transparent',
              borderLeft: 'none',
              borderRight: 'none',
              borderTop: 'none',
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
    ) : (
      <div style={{
        width: 360, height: 520, display: 'flex', flexDirection: 'column', padding: 8,
      }}
      >
        <div style={{ paddingBottom: 8 }}>
          <Input
            value={stepValueSearch}
            onChange={this.onStepValueSearchChange}
            placeholder="Buscar"
          />
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }} onScroll={this.onStepValueListScroll}>
          {stepValueLoading ? (
            <div style={{ padding: 12, textAlign: 'center', color: 'rgba(0,0,0,0.45)' }}>
              Carregando...
            </div>
          ) : (
            (stepValueOptions || []).map((opt) => (
              <button
                type="button"
                key={String(opt.id)}
                onClick={() => this.onSelectStepValueOption(opt)}
                style={{
                  width: '100%',
                  padding: '12px 8px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f0f0f0',
                  color: '#595959',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  background: 'transparent',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderTop: 'none',
                  textAlign: 'left',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  {opt.imageSrc ? (
                    <img
                      src={opt.imageSrc}
                      alt=""
                      className="technique-position-icon"
                      style={{
                        width: 28,
                        height: 28,
                        objectFit: 'contain',
                        flex: '0 0 auto',
                      }}
                    />
                  ) : null}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontWeight:
                          typeof (opt.displayLabel || opt.label) === 'string'
                            ? 500
                            : undefined,
                      }}
                    >
                      {opt.displayLabel || opt.label}
                    </div>
                    {opt.subtitle ? (
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 12,
                          color: 'rgba(0,0,0,0.45)',
                        }}
                      >
                        {opt.subtitle}
                      </div>
                    ) : null}
                  </div>
                </div>
                {opt.kind === 'resource' && opt.hasChildren ? (
                  <span className="technique-step-popover-chevron">{'>'}</span>
                ) : null}
              </button>
            ))
          )}

          {!stepValueLoading && stepValueLoadingMore ? (
            <div style={{ padding: 12, textAlign: 'center', color: 'rgba(0,0,0,0.45)' }}>
              Carregando...
            </div>
          ) : null}

          {!stepValueLoading && !stepValueLoadingMore && (stepValueOptions || []).length === 0 ? (
            <div style={{ padding: 12, textAlign: 'center', color: 'rgba(0,0,0,0.45)' }}>
              Nenhum resultado
            </div>
          ) : null}
        </div>

        <div style={{ paddingTop: 8 }}>
          {stepValueType === 'DRUG' ? (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <Input
                value={stepValueText}
                readOnly={!!stepValueSelectedOptionId}
                onChange={(e) => {
                  this.setState({
                    stepValueText: e.target.value,
                    stepValueSelectedOptionId: null,
                  });
                }}
                onPressEnter={() => this.commitCurrentStepValue()}
                placeholder="Fármaco"
              />
              <Input
                value={stepValueDrugAmount}
                onChange={(e) => {
                  this.setState({ stepValueDrugAmount: e.target.value });
                }}
                ref={(r) => { this.drugAmountInput = r; }}
                onPressEnter={() => this.commitCurrentStepValue()}
                placeholder="Qtd"
                style={{ width: 110 }}
              />
            </div>
          ) : (
            <Input
              value={stepValueText}
              onChange={(e) => {
                this.setState({
                  stepValueText: e.target.value,
                  stepValueSelectedOptionId: null,
                });
              }}
              onPressEnter={() => this.commitCurrentStepValue()}
              placeholder="Digitar manualmente"
            />
          )}
        </div>
      </div>
    );

    const formItemLayout = {
      labelCol: {
        xs: { span: 24 },
      },
      wrapperCol: {
        xs: { span: 24 },
      },
      colon: false,
    };
    return (
      <Content>
        <Spin spinning={loading}>
          <Form onSubmit={this.handleSubmit}>
            <Row>
              <Col xs={24} sm={0}>
                <Button
                  type="primary"
                  className="add-procedure"
                  shape="circle"
                  size="large"
                  htmlType={loading ? 'button' : 'submit'}
                  loading={loading}
                >
                  {loading ? '' : <Icon type="check" fill="#ffffff" />}
                </Button>
              </Col>

              <Col xs={24} sm={20}>
                <h1 className="title">
                  {isEdit ? 'Editar' : 'Nova'}
                  {' '}
                  técnica
                </h1>
              </Col>
              <Col xs={0} sm={4}>
                <Button
                  type="primary"
                  className="add-procedure"
                  htmlType="submit"
                  loading={loading}
                >
                  SALVAR
                </Button>
              </Col>
            </Row>

            <Row gutter={28}>
              <Col md={24} lg={24} xl={12}>
                <FormItem
                  label="TÍTULO"
                  labelCol={formItemLayout.labelCol}
                  wrapperCol={formItemLayout.wrapperCol}
                  colon={formItemLayout.colon}
                >
                  {getFieldDecorator('name', {
                    validateTrigger: 'onBlur',
                    rules: [
                      {
                        required: true,
                        message: 'Preencha o nome da técnica.',
                      },
                      {
                        validator: this.validatorTechniqueName,
                        message: 'Já existe uma técnica desta categoria com este nome.',
                      },
                    ],
                    initialValue: technique.name,
                  })(
                    <Input
                      disabled={isDisabled}
                      autoComplete="off"
                      onChange={(e) => (e.target.value = stringsHelp.firstLetterUpper(
                        e.target.value,
                        true,
                      ))}
                    />,
                  )}
                </FormItem>
              </Col>

              <Col
                xs={24}
                lg={24}
                sm={24}
                xl={7}
                xxl={6}
                style={{ paddingRight: 5, paddingLeft: 5, paddingBottom: 5 }}
              >
                <Form.Item label="CATEGORIA">
                  {getFieldDecorator('categoryId', {
                    rules: [
                      { required: true, message: 'Selecione a categoria' },
                    ],
                    initialValue: technique.technique_category_id || null,
                  })(
                    <Select
                      placeholder="Selecione a categoria"
                      loading={loadingCategories}
                      style={{ width: '100%' }}
                      showAction={['click']}
                      onChange={this.setCategory}
                    >
                      {techniqueCategories.map((cat) => (
                        <Option key={cat.id} value={cat.id}>
                          {cat.name}
                        </Option>
                      ))}
                    </Select>,
                  )}
                </Form.Item>
              </Col>

              <Col md={24} lg={24} xl={24}>
                <FormItem
                  label="ETAPAS DA TÉCNICA"
                  labelCol={formItemLayout.labelCol}
                  wrapperCol={formItemLayout.wrapperCol}
                  colon={formItemLayout.colon}
                >
                  {(() => {
                    const safeSteps = Array.isArray(steps) ? steps : [];
                    const hasSteps = safeSteps.length > 0;

                    return (
                      <>
                        <DndContext
                          sensors={this.dndSensors}
                          collisionDetection={closestCenter}
                          onDragEnd={this.onStepDragEnd}
                        >
                          <SortableContext
                            items={safeSteps.map((step) => step.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {safeSteps.map((step, index) => (
                              <SortableStep
                                key={step.id}
                                id={step.id}
                                disabled={isDisabled}
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  marginBottom: 14,
                                }}
                              >
                                {({ dragHandleProps }) => (
                                  <>
                                    <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', marginBottom: 6 }}>
                                      {index + 1}
                                      º Passo
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                      <Icon
                                        type="minus-circle"
                                        style={{
                                          fontSize: 22,
                                          color: isDisabled ? 'rgba(0,0,0,0.25)' : '#ff4d4f',
                                          cursor: isDisabled ? 'default' : 'pointer',
                                        }}
                                        onClick={() => {
                                          if (isDisabled) return;
                                          this.removeStep(step.id);
                                        }}
                                      />
                                      {step.typeKey === 'FREE_TEXT' ? (
                                        <Input
                                          value={step.text}
                                          placeholder="Descrição livre"
                                          style={{ flex: 1 }}
                                          disabled={isDisabled}
                                          onChange={(e) => this.setFreeTextStepText(step.id, e.target.value)}
                                          onBlur={() => this.finalizeFreeTextStep(step.id)}
                                          ref={(r) => {
                                            if (r) this.freeTextInputRefs[step.id] = r;
                                          }}
                                        />
                                      ) : (
                                        <Popover
                                          trigger="click"
                                          placement="right"
                                          arrowPointAtCenter
                                          overlayClassName="technique-step-popover"
                                          visible={popoverVisible && editingStepId === step.id}
                                          onVisibleChange={(visible) => {
                                            if (!visible) {
                                              this.closeStepValueModal();
                                            }
                                          }}
                                          content={popoverContent}
                                          title={popoverTitle}
                                        >
                                          <Input
                                            readOnly
                                            value={step.text}
                                            placeholder="Toque para editar"
                                            style={{ flex: 1 }}
                                            prefix={
                                              step.typeKey === 'POSITION' && step.optionId
                                                ? (() => {
                                                  const src = this.getPositionIconSrcById(step.optionId);
                                                  return src ? (
                                                    <img
                                                      src={src}
                                                      alt=""
                                                      className="technique-position-icon"
                                                      style={{ width: 20, height: 20, objectFit: 'contain' }}
                                                    />
                                                  ) : null;
                                                })()
                                                : null
                                            }
                                            onClick={() => {
                                              if (isDisabled) return;
                                              this.openEditStep(step.id);
                                            }}
                                          />
                                        </Popover>
                                      )}
                                      <div
                                        {...dragHandleProps}
                                        style={{
                                          padding: '0 6px',
                                          cursor: isDisabled ? 'default' : 'grab',
                                          color: 'rgba(0,0,0,0.45)',
                                          fontSize: 18,
                                          touchAction: 'none',
                                        }}
                                        title="Arraste para reordenar"
                                      >
                                        <Icon type="menu" style={{ fontSize: 22 }} />
                                      </div>
                                    </div>
                                  </>
                                )}
                              </SortableStep>
                            ))}
                          </SortableContext>
                        </DndContext>

                        {hasSteps ? (
                          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 6 }}>
                            <Popover
                              trigger="click"
                              placement="top"
                              arrowPointAtCenter
                              overlayClassName="technique-step-popover"
                              visible={popoverVisible && !editingStepId}
                              onVisibleChange={(visible) => {
                                if (!visible) {
                                  this.closeStepValueModal();
                                }
                              }}
                              content={popoverContent}
                              title={popoverTitle}
                            >
                              <Button
                                shape="circle"
                                size="large"
                                onClick={() => this.openStepTypeModal(null)}
                                disabled={isDisabled}
                                style={{ borderColor: '#13c2c2', color: '#13c2c2' }}
                              >
                                <Icon type="plus" />
                              </Button>
                            </Popover>
                          </div>
                        ) : (
                          <Popover
                            trigger="click"
                            placement="top"
                            arrowPointAtCenter
                            overlayClassName="technique-step-popover"
                            visible={popoverVisible && !editingStepId}
                            onVisibleChange={(visible) => {
                              if (!visible) {
                                this.closeStepValueModal();
                              }
                            }}
                            content={popoverContent}
                            title={popoverTitle}
                          >
                            <div className="form-list">
                              <div className="form-list-item">
                                <button
                                  type="button"
                                  className="form-add-link"
                                  onClick={() => this.openStepTypeModal(null)}
                                  disabled={isDisabled}
                                >
                                  + Adicionar técnicas
                                </button>
                              </div>
                            </div>
                          </Popover>
                        )}
                      </>
                    );
                  })()}
                </FormItem>
              </Col>

              <Col md={24} lg={24} xl={24}>
                <FormItem
                  label="MATERIAIS"
                  labelCol={formItemLayout.labelCol}
                  wrapperCol={formItemLayout.wrapperCol}
                  colon={formItemLayout.colon}
                >
                  {getFieldDecorator('materials', {
                    initialValue: technique.materials,
                  })(
                    <SelectMaterials readOnly={technique.id > 0} />,
                  )}
                </FormItem>
              </Col>
            </Row>

          </Form>
        </Spin>
      </Content>
    );
  }
}

function mapStateToProps(state) {
  const { selects } = state;
  const { technique } = selects;
  return {
    technique,
  };
}

const connected = withRouter(connect(mapStateToProps)(Form.create()(Technique)));
export default connected;
